import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ImageIcon, MapPin, Phone, Plus, Tag, Trash2, Upload } from "lucide-react";
import L from "leaflet";
// The map's own stylesheet: this screen must not depend on another page
// having been visited first for its panes and popups to be laid out.
import "leaflet/dist/leaflet.css";
import {
  type FormEvent,
  type ReactNode,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
} from "react-leaflet";
import { toast } from "sonner";

import { coverOf, readImage } from "@/components/avatar";
import { DetailPanel, FormPanel } from "@/components/resource-page";
import { Button, IconButton } from "@/components/ui/button";
import { ConfirmDialog, Drawer, Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Page, PageHeader, Toolbar } from "@/components/ui/page";
import { PAGER_BARE, PAGER_TIGHT, Pager } from "@/components/ui/pager";
import { SearchInput } from "@/components/ui/search-input";
import { Segmented } from "@/components/ui/tabs";
import { LoadingNote } from "@/components/ui/loading";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Field, Form, Input } from "@/components/ui/field";
import { FkSelect, fetchCompact } from "@/components/ui/fk-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import { can as allowed } from "@/utils/permissions";
import { listResource } from "@/utils/geo";
import { profileQuery } from "@/services/queries";
import { useNarrow } from "@/hooks/use-narrow";
import { useTranslation } from "@/store/locale";
import { useThemeStore } from "@/store/theme";
import { useDelayed } from "@/hooks/use-delayed";
import { useRecordParam } from "@/hooks/use-record-param";
import { cn } from "@/utils/utils";

/** Which of the viewer's two clubs the route sets out from. */
type Origin = "home" | "posted";

interface Listing {
  id: number;
  updated_at?: string;
  name: string;
  category_id: number;
  category_name: string | null;
  city_id: number;
  city_name: string | null;
  full_address: string;
  phone: string | null;
  latitude: number;
  longitude: number;
  /**
   * Data URLs. From the list, the cover alone — a card shows one, and the
   * whole set for twelve cards was a megabyte; the listing read by id carries
   * every photo. `photo_count` says how many there are either way.
   */
  photos: string[];
  photo_count?: number;
  is_favorite: boolean;
}

/**
 * Twenty-five to a page, the number every other list starts at.
 *
 * It was twelve — a number this screen made up because twelve cards fill the
 * panel — and a size picker offering 10, 25, 50, 100 could not show it. The
 * sizes are the app's four; so is the one it opens on.
 */
const LIST_PAGE = 25;

function YellowPagePage() {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // `endsWith("__yellow_page")` matched `view__yellow_page` too, so a reader was
  // shown buttons the API would refuse — and, once the detail panel gained one,
  // a Delete. Each action asks for its own codename.
  const can = (action: string) => allowed(profile, `${action}__yellow_page`);
  const canChange = can("change");
  const canDelete = can("delete");

  const [search, setSearch] = useState("");
  const deferred = useDeferredValue(search);
  const [cityId, setCityId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [deleting, setDeleting] = useState<Listing | null>(null);
  const [refreshingOne, setRefreshingOne] = useState(false);

  // Where the viewer is, for directions. The club they work from today leads —
  // a temporary posting while it applies, their home club otherwise — but on a
  // posting both are real places to set out from, so both are offered and the
  // active one is merely the default.
  const homeId = profile?.club?.id;
  const postedId = profile?.temporary_club?.id;
  const activeOrigin: Origin =
    profile?.has_active_temporary_club && postedId ? "posted" : "home";
  const [pickedOrigin, setPickedOrigin] = useState<Origin | null>(null);
  // Null until they choose, so the default keeps following the posting rather
  // than freezing whatever was active on the first render.
  const originKey = pickedOrigin ?? activeOrigin;

  const club = (id: number | undefined) =>
    queryOptions({
      queryKey: ["/clubs", id],
      queryFn: async ({ signal }) =>
        (
          await api.get<{
            club_name: string;
            latitude: number | null;
            longitude: number | null;
          }>(`/clubs/${id}`, { signal })
        ).data,
      enabled: Boolean(id),
      staleTime: 300_000,
    });
  const homeClub = useQuery(club(homeId));
  const postedClub = useQuery(club(postedId));
  const fromClub = (originKey === "posted" ? postedClub : homeClub).data ?? null;

  // Only worth a switch when there are two places to switch between.
  const originSwitch =
    postedId && homeId ? (
      <Segmented
        label={t("yp.setOutFrom")}
        value={originKey}
        onChange={setPickedOrigin}
        items={[
          {
            value: "home" as const,
            label: homeClub.data?.club_name.toUpperCase() ?? t("yp.homeClub"),
          },
          {
            value: "posted" as const,
            label: postedClub.data?.club_name.toUpperCase() ?? t("yp.temporaryClub"),
          },
        ]}
      />
    ) : null;

  // Ten at a time, with a pager under the list rather than an endless scroll:
  // the map sits beside the list, and a column that grows without end pushes
  // the pair out of step with each other.
  const [page, setPage] = useState(1);
  // Twelve to begin with, because twelve cards is what this panel holds without
  // scrolling — but it is the reader's to change, as on every other list.
  const [size, setSize] = useState(LIST_PAGE);
  const filters = { search: deferred, city_id: cityId, category_id: categoryId };
  const listings = useQuery({
    queryKey: ["/yellow-pages", "page", filters, page, size],
    queryFn: ({ signal }) =>
      listResource<Listing>(
        "/yellow-pages",
        {
          search: deferred,
          city_id: cityId,
          category_id: categoryId,
          page,
          size,
        },
        signal,
      ),
    // The rows stay while the next page is read, so the panel keeps its height
    // and the map beside it does not jump.
    placeholderData: (previous) => previous,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/yellow-pages"] });
  // A refresh keeps the rows, so there is no skeleton to put up — the listings
  // dim and a note says what the wait is for, the way every other list here
  // behaves. Immediate for the controls, delayed for the note: a loader that
  // flashes is a glitch, but a filter that stays live for 300ms is a request
  // queued behind the one already out.
  //
  // Only a read that brings *other* rows — a new search, filter, page or size,
  // where what is on screen is the previous answer held in place. Re-reading the
  // same page underneath the rows already shown is not a wait for anybody: a
  // tag used to refetch the page after saving, and the whole panel dimmed and
  // locked its filters for a change to one icon. That wait is the icon's own.
  const busy = listings.isFetching && (listings.isPending || listings.isPlaceholderData);
  const rereading = useDelayed(busy);
  const rows = listings.data?.items ?? [];
  const total = listings.data?.total ?? 0;
  const pages = listings.data?.pages ?? 1;

  /** Which listing the map is about. Clicking the open one closes it again. */
  const [selected, setSelected] = useState<Listing | null>(null);
  const panels = useRef<HTMLDivElement>(null);
  // Two panels need the width for both: the frame itself, and the list's half
  // of it — which is what `LIST_MIN` is the floor for. Below either, the map
  // moves into the drawer.
  const narrow = useNarrow(panels, Math.max(SIDE_BY_SIDE, LIST_MIN * 2));
  // A filter or a page is a different set of listings; the one the map is about
  // may not be among them, so the map stands down with the rows it belonged to.
  const show = (next: Listing | null) => setSelected(next);
  const refilter = (change: () => void) => {
    change();
    setPage(1);
    setSelected(null);
  };

  // Which listing is open, and which is being edited, both live in the address
  // bar — so a reload comes back to the same place, as on every other screen.
  const {
    editing,
    setEditing,
    loading: openingEdit,
    // No `known` rows on purpose, here and for `view` below: the list carries
    // only each listing's cover photo, so the form and the panel must read the
    // listing by id — opening one from a list row would save it back with the
    // other photos gone.
  } = useRecordParam<Listing>("edit", "yellow-page", "/yellow-pages");
  const {
    editing: viewing,
    setEditing: setViewing,
    loading: openingView,
  } = useRecordParam<Listing>("view", "yellow-page", "/yellow-pages");

  // Who added or last changed a listing: names, not the ids the record holds.
  const users = useQuery({
    queryKey: ["compact", "/users/compact", "all"],
    queryFn: ({ signal }) => fetchCompact("/users/compact", { limit: 200 }, signal),
    staleTime: 300_000,
  });
  const lookup = (_: string, value: string | number | null) =>
    value === null
      ? "—"
      : (users.data?.find((user) => String(user.id) === String(value))?.name ?? String(value));

  const list = useRef<HTMLUListElement>(null);
  /*
   * The pager's own two shapes, at the widths every other list changes at.
   *
   * The list column, not the window: this panel gives half its width to the
   * map on a wide screen and all of it on a narrow one, so the pager can be
   * short of room on a desktop and have plenty on a tablet. `PAGER_TIGHT`
   * drops the numbered window to three; below `PAGER_BARE` the row cannot
   * hold numbers at all and becomes the two-line phone pager.
   */
  const pagerTight = useNarrow(list, PAGER_TIGHT);
  const pagerBare = useNarrow(list, PAGER_BARE);

  const tag = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? api.put(`/yellow-pages/${id}/favorite`) : api.delete(`/yellow-pages/${id}/favorite`),
    // Awaited, so the button's spinner runs until the card shows the new state
    // rather than flicking back to the old icon while the page is re-read.
    onSuccess: async (_, { on }) => {
      await invalidate();
      toast.success(on ? t("yp.tagged") : t("yp.tagRemoved"));
    },
    onError: (error) => toast.error(errorMessage(error, t("yp.tagFailed"))),
  });
  // The one card whose tag is being saved; every other card stays as it was.
  const tagging = (id: number) => tag.isPending && tag.variables?.id === id;
  const remove = useMutation({
    mutationFn: (listing: Listing) =>
      api.delete(`/yellow-pages/${listing.id}`, ifMatch(listing)),
    onSuccess: () => {
      toast.success(t("yp.listingDeleted"));
      setViewing(null);
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("yp.deleteFailed"))),
  });

  // Adding, editing and viewing take the page over, exactly as they do on
  // Provinces — a form that replaces what it is a form for, and a record read
  // where it was opened rather than in a sheet over the list.
  if (editing !== null) {
    return (
      <Page>
        <ListingForm
          listing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      </Page>
    );
  }

  // The listing is read when it is opened, so there is a wait between the click
  // and the panel. The list would otherwise sit there looking as if the click
  // had missed.
  if (openingView || openingEdit) {
    return (
      <Page>
        <PageHeader
          back={
            <button
              type="button"
              onClick={() => {
                setViewing(null);
                setEditing(null);
              }}
              className="text-fg-muted hover:text-fg text-callout"
            >
              ← {t("yp.listing")}
            </button>
          }
          eyebrow={t("yp.listing")}
          title={<Skeleton className="h-7 w-56" />}
        />
        <LoadingNote subject={t("yp.listing").toLowerCase()} />
        <section className="bg-surface-raised rounded-card border-line grid gap-4 border px-6 py-5">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className={index % 3 === 0 ? "h-4 w-1/2" : "h-4 w-2/3"} />
            </div>
          ))}
        </section>
      </Page>
    );
  }

  if (viewing !== null && viewing !== "new") {
    return (
      <Page>
        <DetailPanel
          singular={t("yp.listing")}
          row={viewing}
          labels={{
            category_name: t("ypc.category"),
            city_name: t("geo.city"),
            full_address: t("yp.fullAddress"),
            phone: t("yp.phone"),
            name: t("geo.name"),
          }}
          lookup={lookup}
          onClose={() => setViewing(null)}
          busy={refreshingOne}
          onRefresh={() => {
            setRefreshingOne(true);
            void invalidate().finally(() => setRefreshingOne(false));
          }}
          onEdit={canChange ? () => setEditing(viewing) : undefined}
          onDelete={canDelete ? () => setDeleting(viewing) : undefined}
          hide={["latitude", "longitude", "is_favorite"]}
          extra={<ListingMap listing={viewing} from={fromClub} originSwitch={originSwitch} />}
        />
        <ConfirmDialog
          open={deleting !== null}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (deleting) remove.mutate(deleting);
            setDeleting(null);
          }}
          title={t("yp.deleteListing")}
          message={t("yp.confirmDelete").replace(
            "{name}",
            deleting?.name ?? t("yp.thisListing"),
          )}
        />
      </Page>
    );
  }

  const filtered = Boolean(search || cityId || categoryId);
  const map = selected ? (
    <ListingMap listing={selected} from={fromClub} originSwitch={originSwitch} />
  ) : null;
  /** The map is beside the list, rather than over it in a drawer. */
  const split = map !== null && !narrow;

  return (
    <Page
      fill
      // Split, the page keeps no padding of its own: the list column takes it
      // back below, and the map has the rest of the frame — edge to edge, and
      // without being pulled out of a box that would clip it.
      // `overflow-hidden` with it: the frame is exactly the height it was
      // given, and a stray pixel inside it — a border, a rounding — must not
      // turn the whole page into a scroller behind a full-height map.
      className={cn(split && "overflow-hidden px-0 pt-0 pb-0 md:px-0")}
    >
      {/* The listings on the left — title and all — and the map for the one
          that is open on the right. The map is not a second
          page to go to and come back from: it belongs beside the list it was
          opened from, so the next listing is one click away. With nothing open
          the list has the width to itself. */}
      <div
        ref={panels}
        className={cn(
          // `minmax(0,1fr)` for the row, not the default `auto`: an auto row is
          // as tall as whatever is in it, so the map column grew past the frame
          // and took the page with it — a scrollbar down the whole window and a
          // drop of empty space under the list. Capped, each column scrolls
          // inside its own half instead.
          // `overflow-hidden` as well: the map column is pulled out to the
          // frame's edges, and nothing here may push the page into scrolling.
          "grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] overflow-hidden",
          // Half and half when the map is up, as the old screen had it, with a
          // rule between rather than a gap: the map runs to the edge.
          split && "grid-cols-2",
        )}
      >
        {/* A container in its own right: everything inside sizes itself by
            this column, which halves when the map opens — the window it sits
            in has not moved at all. */}
        <section
          className={cn(
            // `relative` for the same reason the list below it is: narrow, this
            // column is the scroller, and the labels have to resolve against it
            // rather than against the frame it sits in.
            "@container relative flex min-h-0 min-w-0 flex-col",
            split && "border-line border-r px-5 pt-6 pb-5 md:pl-8",
            // Narrow, the whole column scrolls as one piece: a title and three
            // filters pinned above a list with two centimetres to move in is
            // the filters taking the screen from the thing being filtered.
            narrow && "overflow-y-auto",
          )}
        >
          {/* The title, a line about what the list is, and a rule under it.
              The screen is a directory to look things up in: the filters are
              the only controls it carries. */}
          <PageHeader
            className="hairline-b mb-5 shrink-0 pb-4"
            title={t("yp.title")}
            description={t("yp.description")}
          />
          {/* Stacked only when there is genuinely no room: the two pickers
              pair up at 420, and the search joins them on one line at 760.
              Halved for the map, this column is about 500 wide — three rows of
              full-width controls there is most of the list's height spent on
              filters nobody is using. */}
          <Toolbar className="grid grid-cols-1 gap-2.5 @min-[420px]:grid-cols-2 @min-[760px]:grid-cols-[minmax(0,1fr)_11rem_11rem]">
            <SearchInput
              containerClassName="@min-[420px]:col-span-2 @min-[760px]:col-span-1"
              value={search}
              onChange={(event) => refilter(() => setSearch(event.target.value))}
              onClear={() => refilter(() => setSearch(""))}
              aria-busy={busy}
              placeholder={t("yp.searchListings")}
              aria-label={t("yp.searchListings")}
            />
            {/* `contents`, so the fieldset holds these without becoming a grid
                cell of its own. The search box is deliberately outside it:
                typing is what starts the read, and disabling the box mid-word
                is how the members search used to strand people. */}
            <fieldset disabled={busy} className="contents">
              <FkSelect
                endpoint="/cities/compact"
                value={cityId}
                onChange={(next) => refilter(() => setCityId(next))}
                placeholder={t("yp.anyCity")}
              />
              <FkSelect
                endpoint="/yellow-page-categories/compact"
                value={categoryId}
                onChange={(next) => refilter(() => setCategoryId(next))}
                placeholder={t("yp.anyCategory")}
              />
            </fieldset>
          </Toolbar>

          {rereading ? <LoadingNote subject={t("yp.listings")} /> : null}

          <ul
            ref={list}
            aria-busy={busy || undefined}
            className={cn(
              // A container query context: the column halves when the map
              // opens, so the cards inside must answer to the column's width
              // rather than the window's — which has not changed at all.
              //
              // A column of flex items rather than a grid: an `auto` grid track
              // is sized from its items, so one card whose text would not wrap
              // widened the track past the column and the cards were cut off at
              // the edge. Flex items stretch to the line instead.
              // `relative` is load-bearing. Tailwind's `sr-only` is `position:
              // absolute`, and the tag button on every card carries one. With
              // nothing positioned between them and the page frame they
              // resolved against the frame — which is not this scroller, so
              // nothing clipped them: the twelfth card's label sat a thousand
              // pixels below the fold and stretched the page around it. That is
              // the second scrollbar, running down a column of nothing beside
              // the list's own. The same rule as the list table's scroll box.
              "@container relative mt-4 flex flex-col gap-4 pr-1",
              // Its own scroller only while the column around it is fixed. When
              // the column scrolls, a scroller inside a scroller is two lists to
              // move through to reach one row.
              //
              // `min-h-0` goes with the scrolling, and only with it: it is what
              // lets a flex item be made shorter than what is in it. Narrow,
              // with the column scrolling instead and nothing here clipping
              // anything, it let the column squeeze this list to a few hundred
              // pixels while the rows kept their own height and simply spilled
              // out of it — straight over the count and the pager, which sit
              // where the squeezed list ends and were painted under a stack of
              // cards. `shrink-0` instead: the list is as tall as its rows, the
              // pager follows the last of them, and the column scrolls to both.
              narrow
                ? "shrink-0 overflow-visible"
                : "min-h-0 overflow-x-hidden overflow-y-auto",
              "transition-opacity duration-[var(--duration-base)]",
              busy && rows.length > 0 && "opacity-55",
            )}
          >
            {rows.map((row) => {
              const open = selected?.id === row.id;
              return (
                <li key={row.id} className="w-full min-w-0 shrink-0">
                  <article
                    aria-current={open ? "true" : undefined}
                    onClick={() => show(open ? null : row)}
                    className={cn(
                      "bg-surface-raised border-line w-full min-w-0 rounded-xl border p-4",
                      "flex cursor-pointer flex-col gap-3 @min-[480px]:flex-row @min-[480px]:gap-0",
                      "shadow-[var(--shadow-lift)] transition-[box-shadow,border-color]",
                      "duration-[var(--duration-fast)] hover:shadow-[var(--shadow-raised)]",
                      open && "border-accent",
                    )}
                  >
                    {/* 200 wide and as tall as the card, down to 150 and 100 as
                        the room goes, and the full width of the card below that
                        — the old screen's steps, kept. */}
                    <span
                      className={cn(
                        "bg-surface-inset text-fg-subtle grid shrink-0 place-items-center overflow-hidden rounded-lg",
                        "h-40 w-full @min-[480px]:h-auto @min-[480px]:w-25 @min-[480px]:self-stretch",
                        "@min-[768px]:w-37.5 @min-[1024px]:w-50",
                      )}
                    >
                      {row.photos[0] ? (
                        <img src={row.photos[0]} alt="" className="size-full object-cover" />
                      ) : (
                        <ImageIcon className="size-8" aria-hidden />
                      )}
                    </span>

                    <div className="flex w-full min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 @min-[480px]:px-3 @min-[768px]:px-5">
                        <p className="text-accent text-callout font-semibold">
                          {row.category_name}
                        </p>
                        <h2
                          className={cn(
                            "text-heading mt-1.5 text-[18px] leading-[1.3] font-medium",
                            open && "truncate",
                          )}
                        >
                          {row.name}
                        </h2>
                        <p className="text-fg text-callout mt-2 flex min-w-0 items-center gap-1.5 font-medium">
                          <Phone className="text-fg-muted size-4 shrink-0" aria-hidden />
                          {row.phone ?? t("yp.noPhone")}
                        </p>
                        <p className="text-fg-muted text-callout mt-1 flex min-w-0 items-start gap-1.5">
                          <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
                          <span className={cn("min-w-0", open && "truncate")}>
                            {row.full_address}
                            {row.city_name ? `, ${row.city_name}` : ""}
                          </span>
                        </p>
                      </div>

                      <button
                        type="button"
                        aria-pressed={row.is_favorite}
                        aria-busy={tagging(row.id) || undefined}
                        title={row.is_favorite ? t("yp.removeTag") : t("yp.tagFavourite")}
                        disabled={tagging(row.id)}
                        onClick={(event) => {
                          event.stopPropagation();
                          tag.mutate({ id: row.id, on: !row.is_favorite });
                        }}
                        className={cn(
                          "grid size-9 shrink-0 place-items-center rounded-[10px] border transition-colors @min-[768px]:size-11",
                          "disabled:cursor-not-allowed disabled:opacity-70",
                          row.is_favorite
                            ? "border-accent bg-accent-soft text-accent"
                            : "border-line text-fg-subtle hover:border-accent hover:bg-accent-soft hover:text-accent",
                        )}
                      >
                        {tagging(row.id) ? (
                          <Spinner className="size-5" />
                        ) : (
                          <Tag
                            className={cn("size-5", row.is_favorite && "fill-current")}
                            aria-hidden
                          />
                        )}
                        <span className="sr-only">
                          {row.is_favorite ? t("yp.removeTag") : t("yp.tagFavourite")}
                        </span>
                      </button>
                    </div>
                  </article>
                </li>
              );
            })}

            {listings.isPending
              ? Array.from({ length: 4 }, (_, index) => (
                  <li
                    key={`skeleton-${index}`}
                    className="bg-surface-raised rounded-card flex w-full min-w-0 shrink-0 gap-4 p-4 shadow-[var(--shadow-lift)]"
                  >
                    <Skeleton className="size-24 rounded-xl" />
                    <div className="grid flex-1 content-start gap-2 pt-1">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="mt-2 h-3 w-1/3" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </li>
                ))
              : null}

            {!listings.isPending && rows.length === 0 ? (
              <li>
                <EmptyState
                  kind={filtered ? "search" : "empty"}
                  title={t("yp.noListings")}
                  description={filtered ? t("yp.tryDifferent") : t("yp.listingsAppear")}
                />
              </li>
            ) : null}
          </ul>

          {/* The app's pager, the one every list uses: how many a page holds
              and how many there are, then where you are in them. This screen
              drew its own — a count in its own words, no size to choose,
              worded Prev and Next around numbers of its own — so the one thing
              a person does at the bottom of a list was a different control
              here than everywhere else.

              No padding of its own: the card it closes is the panel, and the
              cards above it stand at the panel's edge. */}
          <Pager
            className="mt-3 px-0"
            page={page}
            pages={pages}
            size={size}
            onSize={(next) => {
              setSize(next);
              setPage(1);
              setSelected(null);
            }}
            total={total.toLocaleString()}
            from={total === 0 ? 0 : (page - 1) * size + 1}
            to={(page - 1) * size + rows.length}
            disabled={busy}
            tight={pagerTight}
            bare={pagerBare}
            label={t("yp.listings")}
            onChange={(next) => {
              setPage(next);
              setSelected(null);
              // Back to the top of the list, where the new page starts.
              // jsdom has no scroller, and nor does a browser before paint.
              list.current?.scrollTo?.({ top: 0 });
            }}
          />
        </section>

        {/* Wide enough for both, the map is the second column. Narrow, there is
            no room beside anything, so it comes up over the list instead. */}
        {/* Out to the frame's own edges: the page pads itself, and a map inset
            from the top and the right is a map with a margin round it, which is
            not what a map is for. */}
        {split ? <aside className="min-h-0 min-w-0">{map}</aside> : null}
      </div>

      {/* Mounted only while it is the one in use: a drawer renders its contents
          whether it is open or not, and two maps at once is two sets of tiles
          and two routes fetched for one listing. */}
      {narrow ? (
        <Drawer
          open={Boolean(map)}
          onClose={() => setSelected(null)}
          title={selected?.name ?? t("yp.location")}
          width="lg"
        >
          {map}
        </Drawer>
      ) : null}
    </Page>
  );
}

/**
 * What the two columns need before they can share a line.
 *
 * Both, not either: the pair needs 768 between them, and the list's half of it
 * has to stay wide enough to be a list — a card with a 100px photo, a name, a
 * number and an address. Below that the map takes the screen as a drawer
 * instead, which is the only way it gets room on a phone.
 */
const SIDE_BY_SIDE = 768;
const LIST_MIN = 420;

/**
 * Whether the element has less room than `breakpoint`.
 *
 * The container's width, not the window's: this screen lives inside a shell
 * with a sidebar that collapses, so the same window is two quite different
 * amounts of room. A viewport query answers about the glass; this answers about
 * the space the panels actually have.
 *
 * Unmeasured counts as wide — the first paint puts the map beside the list
 * rather than flashing a drawer open and shut while the observer catches up.
 */
/** Straight-line distance in km between two points — the fallback when no route comes back. */
function kmBetween(a: [number, number], b: [number, number]): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const h =
    Math.sin(rad(lat2 - lat1) / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

interface Route {
  /** Metres and seconds along the road, not across the map. */
  distance: number;
  duration: number;
  /** GeoJSON: longitude first, which is the opposite of what Leaflet wants. */
  geometry: { coordinates: [number, number][] };
}

const metres = (value: number) =>
  value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
const minutes = (seconds: number) => {
  const total = Math.round(seconds / 60);
  return total >= 60 ? `${Math.floor(total / 60)} h ${total % 60} min` : `${total} min`;
};

/** A dot with a label above it, in the app's own tokens rather than Leaflet's blue pin. */
function marker(label: string, tone: "origin" | "target"): L.DivIcon {
  const safe = label.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  const dot = tone === "origin" ? "bg-fg" : "bg-accent";
  return L.divIcon({
    className: "",
    iconAnchor: [0, 0],
    html: `<div class="pointer-events-none -translate-x-1/2 -translate-y-full flex flex-col items-center gap-0.5 whitespace-nowrap">
      <span class="text-fg text-footnote font-semibold drop-shadow-[0_0_2px_var(--surface)]">${safe}</span>
      <span class="${dot} size-2.5 rounded-full ring-2 ring-white translate-y-1"></span>
    </div>`,
  });
}

/** Frames both ends, and the road between them, whenever the route changes. */
/**
 * Keeps the map fitted to the route, and the route to the map's actual size.
 *
 * Leaflet measures its container once, when it is created. Inside the drawer it
 * is created while the sheet is still opening — zero wide — so it asked for the
 * one tile that covered nothing and stopped there. `invalidateSize` on every
 * resize is what tells it the box it is really in; the fit follows, because a
 * fit worked out at the wrong size is the wrong fit.
 */
function FitRoute({ points }: { points: [number, number][] }) {
  const map = useMap();
  // The bounds object is rebuilt every render, so the effect is keyed on where
  // the points actually are. It used to be keyed on how many there were: pick
  // another listing whose route happened to have the same number of points and
  // the map never moved, so it sat on the last fit — which is how a listing in
  // Quezon City came up showing the Indian Ocean.
  const key = points.map(([lat, lng]) => `${lat.toFixed(5)},${lng.toFixed(5)}`).join("|");
  useEffect(() => {
    // `points` is captured deliberately: the observer is rebuilt whenever the
    // route changes, so the closure is never the old one.
    const fit = () => {
      map.invalidateSize();
      if (points.length > 0) {
        map.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 15 });
      }
    };
    fit();
    // The sheet opens over a couple of hundred milliseconds, so the size it
    // ends at is not the size it started at.
    const observer = new ResizeObserver(fit);
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * The way from the club the viewer works from today to the listing — drawn and
 * written out here rather than handed to someone else's site.
 *
 * It used to be an OpenStreetMap embed with a marker and two "directions" links
 * that left the app, and the only distance it could give was across the map
 * rather than along the road. Leaflet was already a dependency for the member
 * map, so the route is a polyline over the same tiles, and the service's own
 * steps become the list under it.
 *
 * It folds away, and it is only ever built for the listing that is open — so
 * "no listing selected" is already "no map", with nothing to close.
 *
 * ponytail: OSRM's public demo server, called from the browser exactly as the
 * tiles are. Self-host it, or put a keyed provider behind `/maps/route`, if
 * this ever carries real traffic.
 */
type TileChoice = "map" | "satellite";

/**
 * Where the tiles come from.
 *
 * Streets from OpenStreetMap — the same tiles the member population map draws,
 * so there is one street provider in the app and not two — and imagery from
 * Esri.
 *
 * The dark theme used to take its streets from CARTO's `dark_all` instead.
 * CARTO now requires an API key for that, and answers a keyless request with a
 * 200 and a tile reading "API KEY REQUIRED" rather than an error, so nothing in
 * the app could tell it had stopped working: the map simply drew that, every
 * tile, under the pins. We hold no such key, so the dark basemap is made here
 * out of the tiles we can have — see `map-dark` in `index.css`.
 */
function tileUrl(layer: TileChoice): string {
  if (layer === "satellite") {
    return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  }
  return "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
}

function ListingMap({
  listing,
  from,
  originSwitch,
}: {
  listing: Listing;
  from: { club_name: string; latitude: number | null; longitude: number | null } | null;
  /** Which club to set out from, when the viewer is posted to a second one. */
  originSwitch: ReactNode;
}) {
  const t = useTranslation();
  const [layer, setLayer] = useState<TileChoice>("map");
  const dark = useThemeStore((state) => state.choice) === "dark";
  const origin: [number, number] | null =
    from && from.latitude !== null && from.longitude !== null
      ? [from.latitude, from.longitude]
      : null;
  const target: [number, number] = [listing.latitude, listing.longitude];

  const route = useQuery({
    queryKey: ["route", origin, target],
    queryFn: async ({ signal }): Promise<Route | null> => {
      if (!origin) return null;
      // Plain `fetch`, not the app's client: this is somebody else's host, and
      // sending our session header to it would be wrong. It takes the signal
      // all the same — a route nobody is waiting for should stop being drawn.
      const path = `${origin[1]},${origin[0]};${target[1]},${target[0]}`;
      const response = await fetch(
        // The line and its length, not the turns: the map shows the way, and
        // a written list of them is not what this panel is for.
        `https://router.project-osrm.org/route/v1/driving/${path}?overview=full&geometries=geojson`,
        { signal },
      );
      if (!response.ok) throw new Error(`Routing failed: ${response.status}`);
      const body = (await response.json()) as { routes?: Route[] };
      return body.routes?.[0] ?? null;
    },
    enabled: origin !== null,
    staleTime: 3_600_000,
    retry: 1,
  });

  // Leaflet takes latitude first; GeoJSON gives longitude first.
  const line: [number, number][] =
    route.data?.geometry.coordinates.map(([lng, lat]) => [lat, lng]) ?? [];
  const points = line.length ? line : origin ? [origin, target] : [target];

  // The road distance when there is a route, the straight line when there is not.
  const summary = route.data
    ? t("yp.byCar")
        .replace("{distance}", metres(route.data.distance))
        .replace("{time}", minutes(route.data.duration))
    : origin
      ? t("yp.straightLine").replace("{km}", kmBetween(origin, target).toFixed(1))
      : `${listing.latitude.toFixed(5)}, ${listing.longitude.toFixed(5)}`;

  return (
    // The panel is the map. Everything it has to say — which listing, how far,
    // how long — is said over the tiles rather than in a column above them, so
    // there is nothing to scroll past to reach it and nothing to scroll at all.
    <div className="relative h-full min-h-72 w-full overflow-hidden [@media(max-height:32rem)]:min-h-0">
      <MapContainer
        key={listing.id}
        center={target}
        zoom={13}
        // The wheel zooms rather than scrolling the page past it: the map is
        // the whole panel, so there is nothing behind it to scroll to.
        scrollWheelZoom
        zoomControl={false}
        // The corner is left clear, as it is on the old screen. Ours is the
        // same OpenStreetMap data, credited in the page's own footer.
        attributionControl={false}
        // Only the street tiles are turned dark, and only under the dark
        // theme: the satellite layer is a photograph, and a photograph put
        // through the same filter is a negative.
        className={cn("h-full w-full", dark && layer === "map" && "map-dark")}
      >
        <TileLayer key={layer} url={tileUrl(layer)} />
        {line.length ? (
          // The colour rides on a class, not the `color` option: Leaflet writes
          // that to the SVG `stroke` attribute, where `var()` does not resolve.
          // A CSS declaration beats a presentation attribute, so the line
          // follows the theme.
          <Polyline positions={line} pathOptions={{ className: "stroke-accent", weight: 5 }} />
        ) : null}
        {origin && from ? (
          <Marker position={origin} icon={marker(from.club_name.toUpperCase(), "origin")} />
        ) : null}
        <Marker position={target} icon={marker(listing.name, "target")} />
        {/* What the old screen said about the listing, where it said it: a
            bubble on the pin, with its own close. */}
        <Popup position={target} autoPan={false}>
          <span className="text-heading text-callout font-semibold">{listing.name}</span>
          <span className="text-accent text-subhead mt-0.5 block">
            {route.isPending && origin ? t("yp.findingWay") : summary}
          </span>
          {originSwitch ? <span className="mt-2 block">{originSwitch}</span> : null}
        </Popup>
        <ZoomControl position="topright" />
        <FitRoute points={points} />
      </MapContainer>

      {/* Streets or imagery, over the map rather than above it: the choice
          sits where the thing it changes is. */}
      <div className="border-line bg-surface-raised absolute top-3 left-1/2 z-[500] flex -translate-x-1/2 gap-0.5 rounded-lg border p-0.5 shadow-[var(--shadow-lift)]">
        {(["map", "satellite"] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            aria-pressed={layer === choice}
            onClick={() => setLayer(choice)}
            className={cn(
              "text-subhead rounded-md px-2.5 py-1 font-medium transition-colors",
              layer === choice
                ? "bg-accent text-accent-fg"
                : "text-fg-muted hover:bg-surface-inset hover:text-fg",
            )}
          >
            {t(choice === "map" ? "yp.layerMap" : "yp.layerSatellite")}
          </button>
        ))}
      </div>
    </div>
  );
}

const MAX_PHOTOS = 5;

/** The panel footer's Save reaches the form by name; only one is ever open. */
const FORM_ID = "listing-form";

function ListingForm({
  listing,
  onClose,
  onSaved,
}: {
  listing: Listing | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();

  const [values, setValues] = useState({
    name: listing?.name ?? "",
    category_id: listing?.category_id.toString() ?? "",
    city_id: listing?.city_id.toString() ?? "",
    full_address: listing?.full_address ?? "",
    phone: listing?.phone ?? "",
    latitude: listing?.latitude.toString() ?? "",
    longitude: listing?.longitude.toString() ?? "",
  });
  const [photos, setPhotos] = useState<string[]>(listing?.photos ?? []);
  const [error, setError] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState<string | null>(null);
  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }));

  const addCategory = async (name: string) => {
    if (!name.trim()) return;
    try {
      const created = (
        await api.post<{ id: number; name: string }>("/yellow-page-categories", {
          name: name.trim(),
        })
      ).data;
      await queryClient.invalidateQueries({ queryKey: ["options", "yellow-page-categories"] });
      set("category_id", String(created.id));
      setNewCategory(null);
      toast.success(t("yp.categoryAdded").replace("{name}", created.name));
    } catch (err) {
      toast.error(errorMessage(err, t("yp.categoryFailed")));
    }
  };

  const addPhotos = async (files: FileList | File[] | null) => {
    if (!files) return;
    const room = MAX_PHOTOS - photos.length;
    const picked = [...files].filter((f) => f.type.startsWith("image/")).slice(0, room);
    if (picked.length === 0) return;
    try {
      const urls = await Promise.all(picked.map((file) => readImage(file, 800)));
      setPhotos((current) => [...current, ...urls].slice(0, MAX_PHOTOS));
    } catch (err) {
      toast.error(errorMessage(err, t("yp.imageFailed")));
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      // The list's thumbnail, from the first photo. Sent with every save that
      // carries photos, so it always matches them; without photos, nothing.
      const cover = photos[0] ? await coverOf(photos[0]) : null;
      const body = {
        name: values.name,
        category_id: Number(values.category_id),
        city_id: Number(values.city_id),
        full_address: values.full_address,
        phone: values.phone || null,
        latitude: Number(values.latitude),
        longitude: Number(values.longitude),
        photos,
        ...(cover ? { cover } : {}),
      };
      return listing
        ? api.patch(`/yellow-pages/${listing.id}`, body, ifMatch(listing))
        : api.post("/yellow-pages", body);
    },
    onSuccess: () => {
      toast.success(listing ? t("yp.listingUpdated") : t("yp.listingAdded"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("yp.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  const req = (label: string) => (
    <>
      {label}
      <span aria-hidden className="text-danger ml-0.5">
        *
      </span>
    </>
  );

  return (
    <FormPanel
      title={listing ? t("yp.editListing") : t("yp.addListing")}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <Form id={FORM_ID} onSubmit={submit} className="grid gap-4 md:grid-cols-2 md:items-start">
        <Field label={req(t("geo.name"))} htmlFor="yp-name">
          <Input
            id="yp-name"
            required
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label={req(t("yp.category"))} htmlFor="yp-category">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <FkSelect
                id="yp-category"
                required
                endpoint="/yellow-page-categories/compact"
                value={values.category_id}
                onChange={(next) => set("category_id", next)}
                placeholder={t("yp.selectCategory")}
              />
            </div>
            <IconButton
              label={t("yp.addCategory")}
              variant="secondary"
              onClick={() => setNewCategory("")}
            >
              <Plus aria-hidden />
            </IconButton>
          </div>
        </Field>
        <Field label={req(t("yp.location"))} htmlFor="yp-city" hint={t("yp.locationHint")}>
          <FkSelect
            id="yp-city"
            required
            endpoint="/cities/compact"
            value={values.city_id}
            onChange={(next) => set("city_id", next)}
            placeholder={t("yp.selectCity")}
          />
        </Field>
        <Field label={req(t("geo.longitude"))} htmlFor="yp-lng">
          <Input
            id="yp-lng"
            type="number"
            step="any"
            min={-180}
            max={180}
            required
            value={values.longitude}
            onChange={(e) => set("longitude", e.target.value)}
          />
        </Field>
        <Field label={req(t("yp.fullAddress"))} htmlFor="yp-address">
          <Input
            id="yp-address"
            required
            value={values.full_address}
            onChange={(e) => set("full_address", e.target.value)}
          />
        </Field>
        <Field label={req(t("geo.latitude"))} htmlFor="yp-lat">
          <Input
            id="yp-lat"
            type="number"
            step="any"
            min={-90}
            max={90}
            required
            value={values.latitude}
            onChange={(e) => set("latitude", e.target.value)}
          />
        </Field>
        <Field label={t("yp.phone")} htmlFor="yp-phone">
          <Input
            id="yp-phone"
            type="tel"
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>

        <div className="md:col-span-2">
          <span className="text-fg text-body font-semibold">{t("yp.photos")}</span>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void addPhotos(e.dataTransfer.files);
            }}
            className="border-line text-fg-muted hover:border-accent text-callout mt-1.5 flex min-h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 transition-colors"
          >
            <Upload className="size-6" aria-hidden />
            {t("yp.dropPhotos")}
            <span className="text-fg-subtle text-footnote">
              {t("yp.photosOf")
                .replace("{n}", String(photos.length))
                .replace("{max}", String(MAX_PHOTOS))}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={(e) => {
                void addPhotos(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          {photos.length ? (
            <ul className="mt-3 flex flex-wrap gap-3">
              {photos.map((src, index) => (
                <li key={index} className="relative">
                  <img src={src} alt="" className="size-20 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((p) => p.filter((_, i) => i !== index))}
                    className="bg-surface-raised text-fg-muted hover:text-danger absolute -top-2 -right-2 grid size-6 place-items-center rounded-full shadow"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    <span className="sr-only">{t("yp.removePhoto")}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5 md:col-span-2"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </p>
        ) : null}

        <Modal
          open={newCategory !== null}
          onClose={() => setNewCategory(null)}
          size="sm"
          title={t("yp.newCategory")}
          description={t("yp.newCategoryHint")}
          footer={
            <>
              <Button variant="secondary" onClick={() => setNewCategory(null)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void addCategory(newCategory ?? "")}>{t("yp.add")}</Button>
            </>
          }
        >
          <Field label={t("geo.name")} htmlFor="yp-new-category">
            <Input
              id="yp-new-category"
              autoFocus
              required
              maxLength={80}
              value={newCategory ?? ""}
              onChange={(event) => setNewCategory(event.target.value)}
            />
          </Field>
        </Modal>
      </Form>
    </FormPanel>
  );
}

export default YellowPagePage;
